'use client';

/**
 * Settings — the way back into an answer that used to be final.
 *
 * IdentityGate asks who somebody is once, and then never again: it opens on
 * `needsName`, and answering it makes that false for good. Which left the one
 * obvious question it could not answer — "I typed the wrong thing / I moved
 * team / we changed our name / that is not my name" — with no route at all.
 * This is that route, and it is deliberately the SAME questions and the same
 * endpoint, so there is one story about what a person is called and not two.
 *
 * THE OPPOSITE OF THE GATE, in every way that dialog is unusual. It has a ✕, it
 * closes on Escape, it closes on the backdrop, and it can be opened by anyone at
 * any time whether or not they have ever answered. Nothing about it is
 * mandatory, because nothing about a correction can be: a person who opens this
 * and changes their mind has to be able to leave without writing anything.
 *
 * WHY TABS FOR ONE TAB. Because the one it has is a category rather than a
 * screen — a settings dialog whose entire content is two fields will grow a
 * second thing to hold, and the shape that accommodates that is the shape that
 * also tells the reader what they are looking at. The rail is one line of markup
 * per tab; retro-fitting it later means moving every field in here.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useIdentity, nicknameField, NO_TEAM } from '@/lib/identity';
import { randomCompanyPlaceholder } from '@/lib/company';
import { S_BTN, S_BTN_GHOST, S_BTN_PRIMARY, S_INPUT, S_MUTED } from './settingsUi';
import { GenderPicker } from './AuthForm';
import { BRAND } from '@/lib/brand';
import type { Gender } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { removeAvatar } from '@/lib/api';
import FlagSettings from './FlagSettings';
import AppearanceSettings from './AppearanceSettings';
import CreditSettings from './CreditSettings';
import RelationshipSettings from './RelationshipSettings';
import NotificationSettings from './notifications/NotificationSettings';
import { beginLeaveWait } from '@/lib/wait';
import Layer from './Layer';

type Tab = 'profile' | 'notifications' | 'credit' | 'relationship' | 'appearance' | 'flags';
/** For a caller that opens the dialog on a particular tab (the room bar's profile menu). */
export type SettingsTab = Tab;

/** Each tab's header line and sidebar group. */
const TAB_META: Record<Tab, { title: string; hint: string; group: 'account' | 'preferences' }> = {
  profile: { title: 'Profile', hint: `How you appear across ${BRAND.name}.`, group: 'account' },
  notifications: { title: 'Notifications', hint: 'Everything that happened to you, and what to be told about.', group: 'account' },
  credit: { title: 'Credit', hint: 'Your KHARMA balance and where it went.', group: 'account' },
  relationship: { title: 'Relationship', hint: 'People you have played online co-op with, closest first.', group: 'account' },
  appearance: { title: 'Appearance', hint: 'Light or dark, and the font the whole page is set in.', group: 'preferences' },
  flags: { title: 'Flags', hint: 'Choose which country flags appear across the app.', group: 'preferences' },
};

const TABS: { id: Tab; label: string; icon: JSX.Element }[] = [
  {
    id: 'profile',
    label: 'Profile',
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="8" r="3.6" />
        <path d="M4.5 20a7.5 7.5 0 0115 0" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'notifications',
    label: 'Notifications',
    /* A bell, the same mark as the header's. */
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor"
           strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 9.5a6 6 0 0112 0c0 4.5 1.8 6.5 2.5 7.5h-17C4.2 16 6 14 6 9.5z" />
        <path d="M10 20a2 2 0 004 0" />
      </svg>
    ),
  },
  {
    id: 'credit',
    label: 'Credit',
    /*
      A coin seen face-on, not a stack and not a banknote. A stack reads as
      "statistics" at 16px and a note reads as "money", and KHARMA is neither — it
      is one token with a face, which is what the header draws too. The inner
      circle is what keeps it from being mistaken for the theme toggle's sun.
    */
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor"
           strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="4" />
      </svg>
    ),
  },
  {
    id: 'relationship',
    label: 'Relationship',
    /* Two people side by side — the people you have played with. */
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor"
           strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="8.5" cy="8" r="3" />
        <circle cx="16" cy="9" r="2.5" />
        <path d="M3 19.5a5.5 5.5 0 0111 0" />
        <path d="M14.5 14.6a4.5 4.5 0 016.5 4.4" />
      </svg>
    ),
  },
  {
    id: 'appearance',
    label: 'Appearance',
    /* Half a disc filled: light and dark in one mark. */
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor"
           strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 3.5a8.5 8.5 0 010 17z" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    id: 'flags',
    label: 'Flags',
    /*
      A pennant on a pole, not a rectangle: at 16px a rectangle with a line down
      one side is indistinguishable from the “document” icon every other rail in
      this product uses, and the pole plus the notch is what makes it read as a
      flag rather than as a page.
    */
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor"
           strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 21V4" />
        <path d="M6 4.5h11.5l-2.6 3.75L17.5 12H6" />
      </svg>
    ),
  },
];

export default function SettingsDialog({
  onClose,
  /**
   * Nobody has told us who they are yet, so this is the wall rather than the
   * settings panel — and there is no way out of it until both answers are in.
   *
   * ONE COMPONENT, TWO MODES, and that is the point: the questions, the team-0
   * rule, the validation and the write are identical either way, so they are
   * written once. What the flag changes is only the ways OUT — the ✕, Escape and
   * the backdrop all disappear, focus is held inside the card, and the z-index
   * goes above everything else on the page. A second component would be a second
   * place for the nickname rules to drift.
   */
  mandatory = false,
  initialTab,
}: {
  onClose: () => void;
  mandatory?: boolean;
  /** Open on this tab instead of the default one. Ignored for a guest, who has only Flags. */
  initialTab?: Tab;
}) {
  const identity = useIdentity();
  const auth = useAuth();
  const { user } = auth;
  // A guest has no account, so the Profile tab (nickname, team, company, sign
  // out) is not theirs to use — the Flags tab is, and it saves to their browser.
  // So a guest sees the dialog opened straight on Flags, with Profile hidden.
  const isGuest = !user;
  const [tab, setTab] = useState<Tab>(isGuest ? 'flags' : (initialTab ?? 'profile'));

  /*
    THE WALL HAS ONE TAB, and it is not a styling choice.

    In `mandatory` mode this dialog is the only thing on screen and there is no
    way out of it until the nickname and the team are in. A rail that can take
    somebody to a flag list is a rail that can take them somewhere they cannot
    answer the question from — and the focus trap would then be holding them in
    a panel of switches with no Save button in sight.
  */
  const tabs = mandatory
    ? TABS.filter((t) => t.id === 'profile')
    : isGuest
      // A guest has free translations rather than a KHARMA wallet, so Credit is
      // an account tab exactly as Profile is — see lib/marq.ts on why the
      // token is attached to a person and not to an address.
      ? TABS.filter((t) => t.id === 'appearance' || t.id === 'flags')
      : TABS;

  const [nickname, setNickname] = useState(identity.nickname);
  const [team, setTeam] = useState<number | null>(identity.team);
  /** Who they work for — `company`-`team` is the label. See lib/company.ts. */
  const [company, setCompany] = useState(identity.company);
  /** A made-up example in the empty box — picked after mount (Math.random would
   *  differ between the server render and hydration). */
  const [companyPlaceholder, setCompanyPlaceholder] = useState('');
  useEffect(() => { setCompanyPlaceholder(randomCompanyPlaceholder()); }, []);
  /**
   * Has the person in front of this dialog changed anything yet?
   *
   * Guards the fill below. Without it, an answer arriving from the server while
   * somebody is mid-word would overwrite what they are typing.
   */
  const touched = useRef(false);
  const [gender, setGender] = useState<Gender | null>(user?.gender ?? null);
  useEffect(() => { if (!touched.current) setGender(user?.gender ?? null); }, [user?.gender]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const card = useRef<HTMLDivElement | null>(null);
  const first = useRef<HTMLInputElement | null>(null);

  const clearAvatar = useCallback(async () => {
    if (avatarBusy) return;
    setAvatarBusy(true);
    setAvatarError(null);
    try {
      await removeAvatar();
      identity.setAvatarUrl(null);
    } catch (e) {
      setAvatarError((e as Error).message);
    } finally {
      setAvatarBusy(false);
    }
  }, [avatarBusy, identity]);

  // Escape closes — unless this is the wall, which has no way out at all.
  // The listener is on the window so it works with focus anywhere inside.
  useEffect(() => {
    if (mandatory) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, mandatory]);

  // Nothing behind it should move; a dialog you can scroll the page behind reads
  // as a bug rather than as a decision.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  // Straight into the box most people opened this to change.
  useEffect(() => { first.current?.focus(); }, []);

  /*
   * FILL BOTH BOXES FROM THE ACCOUNT, and re-fill them whenever it changes.
   *
   * The two `useState` calls above only run on the FIRST render, so before
   * accounts existed this dialog could open on an empty nickname and a
   * blank team: the identity lookup is asynchronous, and the gear is reachable
   * the moment the header paints. Now that the answer comes from the signed-in
   * account (lib/identity.tsx reads it from lib/auth.tsx), it also changes
   * without this dialog being remounted — rename yourself, close, reopen, and
   * the old name would still be sitting in the box.
   *
   * `touched` is what keeps it from fighting the person typing: once they have
   * edited either field, nothing arriving later overwrites them.
   */
  useEffect(() => {
    if (touched.current) return;
    setNickname(identity.nickname);
    setTeam(identity.team);
    setCompany(identity.company);
  }, [identity.nickname, identity.team, identity.company]);

  /*
   * Keep the keyboard inside the card — wall only.
   *
   * The page behind is covered, not removed: its textarea, its mode column and
   * its header buttons are all still in the document and still focusable, so Tab
   * would walk somebody into controls they cannot see and let them type into a
   * box nobody is looking at.
   *
   * TWO listeners, and the second is the one that is easy to miss. `focusin`
   * catches focus ARRIVING outside — Tab into the page behind, find-in-page, a
   * screen reader's cursor. But clicking the backdrop focuses NOTHING: it blurs
   * the input and leaves `document.activeElement` as `<body>`, and no `focusin`
   * ever fires, so a focusin-only trap leaves the dialog with no focused control
   * and the next keystroke going nowhere. That is measured, not theoretical.
   * `focusout` covers it, one tick later, once focus has settled.
   */
  useEffect(() => {
    if (!mandatory) return;
    const hold = () => {
      const active = document.activeElement;
      if (card.current && active && card.current.contains(active)) return;
      first.current?.focus();
    };
    hold();
    const onFocusIn = () => hold();
    // A tick late on purpose: during `focusout`, `activeElement` is still the
    // element being left, so checking immediately would always pull focus back.
    const onFocusOut = () => window.setTimeout(hold, 0);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [mandatory]);

  /*
   * The same team-0 rule the wall applies, from the same helper — see
   * `nicknameField`. Two dialogs asking one question must not answer it twice.
   *
   * `field.value` is both what the box shows and what is saved; the `nickname`
   * state underneath is never overwritten, so trying team 0 here and going back
   * to team 2 returns whatever was typed rather than a name the app chose.
   */
  const field = nicknameField(team, nickname, identity.noTeamNickname);
  const clean = field.value.trim();

  const cleanCompany = company.trim();

  /*
   * Team 0 needs no nickname: the server derives it. Every other team is
   * unchanged and still needs both answers.
   *
   * The company is OPTIONAL (2026-10-06, owner's call) — same as sign-up.
   * For an account the nickname is needed on every team, team 0 included:
   * only anonymous claims get the server-derived name.
   */
  // The profile no longer asks for a team (owner 2026-10-11): an account keeps
  // the one it has. Only the wall still asks, so only the wall requires it.
  const saveTeam = team ?? NO_TEAM;
  const ready = (!mandatory || team !== null) && (!user || !!gender) && (!!clean || (saveTeam === NO_TEAM && field.locked));

  const submit = useCallback(async () => {
    if (!ready || saving) return;
    setSaving(true);
    setError(null);
    try {
      // Company is no longer collected: '' leaves whatever the account has untouched.
      await identity.save(clean, '', saveTeam);
      if (user && gender && gender !== user.gender) await auth.update({ gender });
      /*
       * Closed the moment the write lands, in both modes.
       *
       * It used to stay open on a "Saved —" line, on the reasoning that somebody
       * who came here to correct their name wants to see that it took. In
       * practice the dialog is the only thing on screen and staying open reads
       * as the Save not having worked, which is the doubt that makes people
       * press it twice. The change IS the confirmation: the header behind shows
       * the new name, and the wall it was covering is gone.
       *
       * In the wall's case this is belt AND braces — `identity.save()` has
       * already set `gateOpen` false, which unmounts this from the other side.
       * Calling it anyway keeps one rule ("Save closes it") instead of two.
       */
      onClose();
    } catch (e) {
      // The form keeps both answers. A failure that cleared it would lose the
      // correction the person came here to make.
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, [clean, saveTeam, ready, saving, identity, onClose, user, gender, auth]);

  const meta = TAB_META[tab];
  const title = mandatory ? 'Tell us who you are' : meta.title;
  const hint = mandatory
    ? 'Your work is saved under a name we made up for you. Put your own nickname here before you start.'
    : tab === 'profile' && identity.unnamed
      ? 'You are still listed under a name we made up for you — put your own here.'
      : meta.hint;
  const groups = (['account', 'preferences'] as const)
    .map((g) => ({ g, items: tabs.filter((t) => TAB_META[t.id].group === g) }))
    .filter((x) => x.items.length > 0);

  return (
    <Layer>
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
      /*
        Tagged so the rest of the app can ask "is the page blocked?" without
        importing anything. The translator's window-level Ctrl+Enter handler
        reads exactly this — focus is trapped in the card, but a chord bound to
        the window would still fire from a team button, and "no actions" has to
        mean no actions.
      */
      data-gt-modal={mandatory ? 'mandatory' : 'settings'}
      /*
        z-[130] as the wall, z-[120] as the panel — the wall must sit above the
        translator's own dialogs; the ordinary panel stays below anything that
        genuinely must not be covered.
      */
      className={
        'fixed inset-0 flex items-center justify-center p-3 sm:p-6 ' +
        (mandatory
          ? 'z-[130] bg-black/60 backdrop-blur-[3px]'
          : 'z-[120] bg-black/45 backdrop-blur-[2px]')
      }
      onMouseDown={(e) => {
        if (!card.current || card.current.contains(e.target as Node)) return;
        /*
          The backdrop closes the panel and does NOTHING to the wall — except
          prevent the default, which is what stops a press there from BLURRING
          the card.
        */
        if (mandatory) e.preventDefault();
        else onClose();
      }}
    >
      <div
        ref={card}
        /*
          ONE SIZE, WHATEVER THE TAB IS ON (owner 2026-10-11 redesign): a wide,
          fixed-height panel the tabs live INSIDE, so moving between them never
          makes the window jump. Each tab scrolls in its own pane.
        */
        className="flex h-[min(640px,calc(100dvh-1.5rem))] w-full max-w-[880px] flex-col overflow-hidden
                   rounded-2xl border border-ink-200 bg-white text-ink-900
                   shadow-[0_30px_90px_-25px_rgba(0,0,0,0.45)] sm:flex-row
                   dark:border-white/10 dark:bg-ink-900 dark:text-ink-dim"
      >
        {/* ── Sidebar: who you are, the tabs, the way out ── */}
        <aside className="flex shrink-0 flex-col border-b border-ink-200 bg-ink-100/60 sm:w-60 sm:border-b-0 sm:border-r
                          dark:border-white/10 dark:bg-ink-950/50">
          <SidebarIdentity avatarUrl={identity.avatarUrl} />
          <nav
            role="tablist"
            aria-orientation="vertical"
            className="flex gap-1 overflow-x-auto px-2 py-2 sm:flex-col sm:overflow-visible sm:px-3 sm:py-0"
          >
            {groups.map(({ g, items }) => (
              <div key={g} className="contents sm:block sm:pb-4">
                <p className="hidden px-2.5 pb-1.5 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-400 sm:block dark:text-ink-500">
                  {g === 'account' ? 'Account' : 'Preferences'}
                </p>
                <div className="contents sm:flex sm:flex-col sm:gap-0.5">
                  {items.map((t) => {
                    const on = tab === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        onClick={() => setTab(t.id)}
                        className={
                          'flex shrink-0 items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition ' +
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-400 ' +
                          (on
                            ? 'bg-white font-medium text-ink-900 shadow-sm ring-1 ring-ink-200 ' +
                              'dark:bg-white/[0.08] dark:text-white dark:ring-white/10'
                            : 'text-ink-600 hover:bg-white/70 hover:text-ink-900 ' +
                              'dark:text-ink-300 dark:hover:bg-white/[0.04] dark:hover:text-ink-dim')
                        }
                      >
                        <span className={on ? 'text-ink-900 dark:text-white' : 'text-ink-400 dark:text-ink-500'}>{t.icon}</span>
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
          <div className="mt-auto hidden border-t border-ink-200 p-3 sm:block dark:border-white/10">
            <SignOutButton mandatory={mandatory} />
          </div>
        </aside>

        {/* ── The tab ── */}
        <section className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header className="flex items-start justify-between gap-4 border-b border-ink-200 px-6 pb-4 pt-5 sm:px-8 sm:pt-6 dark:border-white/10">
            <div className="min-w-0">
              <h2 id="settings-title" className="text-lg font-semibold tracking-tight text-ink-900 dark:text-white">
                {title}
              </h2>
              <p className="mt-1 text-sm text-ink-500 dark:text-ink-400">{hint}</p>
            </div>
            {/* No ✕ on the wall, and that is the point of the mode. */}
            {mandatory ? (
              <span className="mt-1 shrink-0 rounded-full border border-ink-200 px-2.5 py-1 text-[11px] font-medium text-ink-600
                               dark:border-white/10 dark:text-ink-300">
                Required
              </span>
            ) : (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close settings"
                className="-mr-2 -mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-lg text-ink-400 transition
                           hover:bg-ink-100 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-400
                           dark:hover:bg-white/5 dark:hover:text-white"
              >
                <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor"
                     strokeWidth="1.8" strokeLinecap="round">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            )}
          </header>

          <div role="tabpanel" className="min-h-0 flex-1 overflow-y-auto px-6 py-6 sm:px-8">
            {tab === 'profile' && (
              <form
                id="gt-profile-form"
                onSubmit={(e) => { e.preventDefault(); void submit(); }}
                className="divide-y divide-ink-200 dark:divide-white/10"
              >
                <Row label="Photo" hint="Your TrueMarch Tiger is your picture everywhere.">
                  <div className="flex items-center gap-4">
                    <span className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-full border border-ink-200
                                     bg-ink-100 dark:border-white/10 dark:bg-ink-950">
                      {identity.avatarUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element -- a 64px avatar from our own API
                        <img src={identity.avatarUrl} alt="Your avatar" className="h-full w-full object-cover" />
                      ) : (
                        <span className="text-lg font-semibold text-ink-500 dark:text-ink-400">{initialsOf(clean || identity.nickname)}</span>
                      )}
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link href="/avatar" onClick={onClose} className={S_BTN}>
                          Edit Avatar
                        </Link>
                        {identity.avatarUrl && (
                          <button type="button" disabled={avatarBusy} onClick={() => { void clearAvatar(); }} className={S_BTN_GHOST}>
                            Remove
                          </button>
                        )}
                      </div>
                      <p className={'mt-2 text-xs ' + (avatarError ? 'text-red-600 dark:text-red-400' : S_MUTED)} role="status">
                        {avatarError ?? 'Build your own character with clothes, colors and accessories.'}
                      </p>
                    </div>
                  </div>
                </Row>

                <Row label="Nickname" hint="How you appear to everyone.">
                  <input
                    ref={first}
                    value={field.value}
                    onChange={(e) => { touched.current = true; setNickname(e.target.value); }}
                    maxLength={identity.maxNickname}
                    autoComplete="nickname"
                    placeholder="What should we call you?"
                    /* readOnly rather than disabled: a disabled field cannot be focused, selected or copied. */
                    readOnly={field.locked}
                    aria-readonly={field.locked}
                    className={S_INPUT + (field.locked ? ' cursor-default bg-ink-100 text-ink-500 dark:bg-white/[0.03] dark:text-ink-400' : '')}
                  />
                  {field.locked && (
                    <p className={'mt-2 text-xs ' + S_MUTED}>
                      Without a team you are saved as <strong className="font-medium text-ink-700 dark:text-ink-200">{identity.noTeamNickname}</strong>.
                    </p>
                  )}
                </Row>

                {/* Company is no longer asked (owner 2026-10-12); gender took its place. */}
                {user && (
                  <Row label="Gender">
                    <div className="max-w-[260px]">
                      <GenderPicker value={gender} onChange={(g) => { touched.current = true; setGender(g); }} />
                    </div>
                  </Row>
                )}

                {/*
                  Team: asked ONLY on the wall (an anonymous visitor's first
                  answer). An account keeps the team it has, untouched — the
                  owner (2026-10-11) took the number buttons off the profile.
                */}
                {mandatory && (
                  <Row label="Team" hint="0 if you are not on one of the teams.">
                    <div role="radiogroup" aria-label="Team number"
                         className="inline-flex rounded-lg border border-ink-200 p-0.5 dark:border-white/10">
                      {identity.teams.map((n) => {
                        const on = team === n;
                        return (
                          <button
                            key={n}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            onClick={() => { touched.current = true; setTeam(n); }}
                            className={
                              'h-8 min-w-[2.75rem] rounded-md px-3 text-sm font-medium tabular-nums transition ' +
                              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-400 ' +
                              (on ? 'bg-ink-900 text-white dark:bg-ink-dim dark:text-ink-950'
                                  : 'text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-white/5')
                            }
                          >
                            {n}
                          </button>
                        );
                      })}
                    </div>
                  </Row>
                )}

                <div className="pt-5 sm:hidden">
                  <SignOutButton mandatory={mandatory} />
                </div>
              </form>
            )}

            {tab === 'notifications' && <NotificationSettings onTab={(t) => setTab(t as Tab)} onClose={onClose} />}
            {tab === 'credit' && <CreditSettings />}
            {tab === 'relationship' && <RelationshipSettings />}
            {tab === 'appearance' && <AppearanceSettings />}
            {tab === 'flags' && <FlagSettings />}
          </div>

          {tab === 'profile' && (
            <footer className="flex items-center justify-end gap-3 border-t border-ink-200 px-6 py-3.5 sm:px-8 dark:border-white/10">
              <span
                role={error ? 'alert' : undefined}
                aria-live="polite"
                className={'mr-auto min-w-0 truncate text-xs ' + (error ? 'text-red-600 dark:text-red-400' : S_MUTED)}
              >
                {error ?? (ready ? '' : mandatory ? 'A nickname and a team number are both needed.' : !clean ? 'A nickname is needed.' : 'Choose your gender.')}
              </span>
              {!mandatory && (
                <button type="button" onClick={onClose} className={S_BTN_GHOST}>Cancel</button>
              )}
              <button type="submit" form="gt-profile-form" disabled={!ready || saving} className={S_BTN_PRIMARY}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </footer>
          )}
        </section>
      </div>
    </div>
    </Layer>
  );
}

/** A settings row: the label and its one-line hint on the left, the control on the right. */
function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-2.5 py-5 first:pt-0 sm:grid-cols-[170px_minmax(0,1fr)] sm:gap-8">
      <div>
        <p className="text-sm font-medium text-ink-900 dark:text-ink-dim">{label}</p>
        {hint && <p className={'mt-0.5 text-xs leading-relaxed ' + S_MUTED}>{hint}</p>}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function initialsOf(name: string): string {
  return name.replace(/[^\p{L}\p{N} ]/gu, '').trim().split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2).toUpperCase() || '?';
}

/** The sidebar's top: the account this panel belongs to. */
function SidebarIdentity({ avatarUrl }: { avatarUrl: string | null }) {
  const { user } = useAuth();
  const name = user?.nickname ?? 'Guest';
  const sub = user ? (user.gender === 'female' ? 'Female' : user.gender === 'male' ? 'Male' : `${BRAND.name} account`) : 'Not signed in';
  return (
    <div className="hidden items-center gap-3 px-5 pb-5 pt-6 sm:flex">
      <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full border border-ink-200 bg-white
                       text-sm font-semibold text-ink-600 dark:border-white/10 dark:bg-ink-900 dark:text-ink-300">
        {avatarUrl && user
          // eslint-disable-next-line @next/next/no-img-element
          ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
          : initialsOf(name)}
      </span>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-ink-900 dark:text-white">{name}</p>
        <p className={'truncate text-xs ' + S_MUTED}>{sub}</p>
      </div>
    </div>
  );
}

/**
 * The way out of the account — at the foot of the sidebar (inside Profile on a
 * phone), apart from everything you press to keep going.
 *
 * **Shown on the wall too**, where there is otherwise no way out at all: a
 * person signed in as someone else on a shared machine has exactly one correct
 * move from there, and it is this one.
 */
function SignOutButton({ mandatory }: { mandatory: boolean }) {
  const { loaded, user, signOut } = useAuth();
  const [leaving, setLeaving] = useState(false);
  if (!loaded || !user) return null;
  return (
    <button
      type="button"
      /* `type="button"` is load-bearing: inside the profile form a default button would SUBMIT it. */
      onClick={() => { setLeaving(true); beginLeaveWait('/login', 'Signing you out'); void signOut().finally(() => window.location.assign('/login')); }}
      disabled={leaving}
      className={S_BTN_GHOST.replace('justify-center', 'justify-start') + ' w-full px-2.5 hover:text-red-600 dark:hover:text-red-400'}
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor"
           strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M15 17l5-5-5-5" />
        <path d="M20 12H9" />
        <path d="M12 3H6a2 2 0 00-2 2v14a2 2 0 002 2h6" />
      </svg>
      {leaving ? 'Signing out…' : mandatory ? 'Not you? Sign out' : 'Sign out'}
    </button>
  );
}
